/**
 * O `external_id` do Meta para o pixel do navegador: o mesmo valor que o servidor já manda.
 *
 * O `telegram-bot` envia os marcos do funil do criador à API de Conversões com
 * `external_id = sha256(platform_user.id)` (`VibxLeadIdentity#hashedExternalId`). Sozinho, esse id
 * não casa ninguém: ele só vira chave quando o Meta o vê também num evento do NAVEGADOR, junto com o
 * cookie do pixel. É isto que faz a ponte — o app empurra o id para o `dataLayer`, e a tag
 * "Meta - correspondência avançada" do `GTM-KBF6J9QS` o passa ao `fbq('init', …)`, que é o único
 * lugar onde o pixel aceita dado de correspondência.
 *
 * ## Por que só o id, e não o e-mail
 *
 * O `dataLayer` é legível por todo script da página e sai para todas as tags do container
 * (`utils/gtmEvent.ts`). O id interno não identifica a pessoa fora da VibX; e-mail, mesmo com hash,
 * identifica em qualquer lugar. O hash aqui NÃO esconde o id — ele é sequencial, e o sha256 de um
 * inteiro pequeno se inverte por enumeração. Existe só para o valor bater byte a byte com o do
 * servidor.
 *
 * ## Uma identidade por documento, e o fim dela
 *
 * O `fbevents` só aceita dado de correspondência num `init` enquanto o pixel ainda NÃO tem nenhum
 * (lido no código do `fbevents.js` em 24/09/2026: `isEmptyObject(userData)`, senão
 * `DUPLICATE_PIXEL_ID`), e o `fbq('set', 'userData')` só vale para pixel integrado à Shopify. Ou
 * seja: dentro de um mesmo carregamento de página a identidade é posta uma vez e não sai nem troca.
 *
 * Por isso o fim da sessão SEM recarregar a página — ela dura quatro horas e pode vencer numa rota
 * híbrida — não tem como limpar o id: o que resta é parar o pixel. `endMetaIdentity` empurra
 * `vibx_identidade_encerrada`, e a tag do GTM responde com `fbq('consent', 'revoke')`: dali até o
 * próximo carregamento o pixel não manda nada, em vez de mandar eventos anônimos com o id da conta
 * anterior. O mesmo vale para um id DIFERENTE no mesmo documento, que o pixel não conseguiria trocar.
 */

export const META_IDENTITY_EVENT = 'vibx_identidade';

/** A tag do GTM que escuta este evento revoga o consentimento do pixel. */
export const META_IDENTITY_END_EVENT = 'vibx_identidade_encerrada';

/** O que a tag do GTM lê, pela variável de camada de dados `vibx_external_id`. */
export const META_EXTERNAL_ID_KEY = 'vibx_external_id';

let ultimoEnviado: string | undefined;
let encerrada = false;
/**
 * Muda a cada fim de sessão. Um push que começou antes do fim (o hash é assíncrono) confere isto
 * depois do `await` e desiste: senão a identidade da sessão que acabou seria publicada DEPOIS do
 * logout, e o tráfego anônimo seguinte sairia com ela.
 */
let geracao = 0;

/** `platform_user.id` como inteiro positivo, aceitando o id em texto que a sessão às vezes traz. */
const idValido = (valor: unknown): number | undefined => {
  const numero =
    typeof valor === 'string' && /^\d+$/.test(valor) ? Number(valor) : valor;
  return Number.isInteger(numero) && (numero as number) > 0
    ? (numero as number)
    : undefined;
};

const empurrar = (entrada: Record<string, unknown>) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(entrada);
};

/** sha256 em hexadecimal minúsculo, igual ao `sha256Hex` do servidor. */
export const sha256Hex = async (valor: string): Promise<string> => {
  const bytes = new TextEncoder().encode(valor);
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Empurra o `external_id` de quem está logado, uma vez por carregamento de página.
 *
 * Nunca lança e nunca atrasa quem chama além do hash: roda no caminho do cadastro, antes do evento
 * `cadastro_criado`, e medição não pode custar o cadastro. Sem `crypto.subtle` (contexto não
 * seguro) ou sem id, simplesmente não empurra.
 */
export const pushMetaIdentity = async (platformUserId: unknown) => {
  try {
    if (typeof window === 'undefined' || encerrada) return;
    const id = idValido(platformUserId);
    if (id === undefined || !window.crypto?.subtle) return;

    const minha = geracao;
    const externalId = await sha256Hex(String(id));
    // A sessão acabou enquanto o hash era calculado: esta identidade já não vale.
    if (minha !== geracao || encerrada) return;
    if (externalId === ultimoEnviado) return;
    // Outro id no mesmo documento: o pixel não troca de identidade, então ele para.
    if (ultimoEnviado !== undefined) {
      endMetaIdentity();
      return;
    }

    empurrar({
      event: META_IDENTITY_EVENT,
      [META_EXTERNAL_ID_KEY]: externalId,
    });
    ultimoEnviado = externalId;
  } catch {
    /* Medição não interrompe a navegação nem o cadastro. */
  }
};

/**
 * A sessão identificada acabou sem recarregar a página: para o pixel até o próximo carregamento.
 *
 * Só age se uma identidade foi posta neste documento — sem ela não há o que vazar, e revogar o
 * consentimento de quem nunca logou tiraria a medição do visitante anônimo à toa.
 */
export const endMetaIdentity = () => {
  try {
    if (typeof window === 'undefined' || encerrada) return;
    // Antes de tudo: cancela qualquer push ainda calculando o hash, mesmo sem identidade posta.
    geracao++;
    if (ultimoEnviado === undefined) return;

    empurrar({ event: META_IDENTITY_END_EVENT });
    encerrada = true;
  } catch {
    /* Medição não interrompe a navegação. */
  }
};

/** Só para os testes: volta ao estado de um carregamento novo. */
export const resetMetaIdentityForTests = () => {
  ultimoEnviado = undefined;
  encerrada = false;
  geracao = 0;
};
